import { useEffect, useRef, useState } from 'react';

import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { HIDE_MOBILE_NAV_ON_SCROLL } from 'lib/brandFeatures';

const SCROLL_THRESHOLD_PX = 200;
export const SHOW_MOBILE_NAV_DELAY = 125;

/**
 * Custom React hook to hide the mobile navigation bar on scroll.
 *
 * @param {string} vertical - The vertical/brand identifier.
 * @param {object} options - Options object.
 * @param {string} options.mobileNavInsertVisibility - Determines the visibility/collapse behavior of the mobile nav insert.
 */
export const useHideMobileNavOnScroll = (vertical, {
  mobileNavInsertVisibility,
} = {}) => {
  const hideOnScrollForBrand = getFeatureConfigForBrand(HIDE_MOBILE_NAV_ON_SCROLL, vertical);
  const [hideMobileNav, setHideMobileNav] = useState(false);
  const [hideMobileNavInsert, setHideMobileNavInsert] = useState(mobileNavInsertVisibility === 'disabled');
  const lastScrollY = useRef(0);
  const hideOnScrollTimeout = useRef(null);

  useEffect(() => {
    /**
     * Event handler for the scroll event.
     * sets state for hiding/showing navbar on scroll.
     */
    const onBodyScroll = () => {
      const newScrollY = Math.floor(window.scrollY);

      if (newScrollY === lastScrollY.current) {
        return;
      }

      const isAboveScrollThreshold = window.scrollY > SCROLL_THRESHOLD_PX;
      const isScrollingUp = newScrollY < lastScrollY.current;
      const shouldHideOnMobile = !isScrollingUp && isAboveScrollThreshold;

      if (hideOnScrollForBrand) {
        if (shouldHideOnMobile) {
          setHideMobileNav(true);
          clearTimeout(hideOnScrollTimeout.current);
        } else {
          // Add slight delay when showing mobile nav again, when scrolling up
          hideOnScrollTimeout.current = setTimeout(() => {
            setHideMobileNav(false);
          }, SHOW_MOBILE_NAV_DELAY);
        }
      }

      if (mobileNavInsertVisibility === 'collapseOnScroll') {
        setHideMobileNavInsert(shouldHideOnMobile);
      }

      lastScrollY.current = newScrollY;
    };

    window.addEventListener('scroll', onBodyScroll);

    return () => {
      window.removeEventListener('scroll', onBodyScroll);
    };
  }, [mobileNavInsertVisibility, hideOnScrollForBrand]);

  return { hideMobileNav, hideMobileNavInsert };
};
